import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { Switch } from "@/components/ui/switch";
import { Confirm, ExportProgress, fmtBytes } from "@/components/account/YourData";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "");
const EVERY = { 1: "every month", 3: "every 3 months", 6: "every 6 months" };

function Status({ b }) {
  if (!b.on) return <span data-testid="backup-status">Off</span>;
  if (b.last_status === "failed") return <span data-testid="backup-status" className="text-red-300">On · {EVERY[b.months]} · Last backup failed</span>;
  return (
    <span data-testid="backup-status">
      On · {EVERY[b.months]}{b.last_at ? ` · Last backup ${day(b.last_at)}` : ""}{b.next_at ? ` · Next backup ${day(b.next_at)}` : ""}
      {b.running ? " · Backing up now…" : b.retrying ? " · Trying again soon" : ""}
    </span>
  );
}

function Setup({ b, hasPassword, google, onSaved, onClose }) {
  const [token, setToken] = useState(null);
  const [months, setMonths] = useState(b.on ? b.months : 3);
  const [videos, setVideos] = useState(b.on && !!b.include_videos);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [removePw, setRemovePw] = useState(false);
  const [errs, setErrs] = useState(null);
  const [busy, setBusy] = useState(false);
  const mismatch = pw && pw2 && pw !== pw2;
  const ok = !pw || (pw.length >= 8 && pw === pw2);
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErrs(null);
    try {
      const { data } = await api.put("/backups", { organization_id: b.hub_id, on: true, months, include_videos: videos, password: pw || null, remove_password: removePw, reauth_token: token });
      toast.success(b.on ? "Automatic backups updated" : "Automatic backups are on. The first one is being made now.");
      onSaved(data);
    } catch (x) {
      const f = fieldErrs(x); if (f) setErrs(f); else toast.error(errMsg(x));
      if (x?.response?.data?.detail?.code === "reauth_required") setToken(null);
    } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={b.on ? "Change automatic backups" : "Turn on automatic backups"} testid="backup-dialog"
      description="A fresh copy of the family's data, made for you and only you. Vault-locked items are never included.">
      {!token ? <Confirm hasPassword={hasPassword} google={google} scope="backup" onDone={setToken} /> : (
        <form className="space-y-4" onSubmit={save} data-testid="backup-form">
          <div>
            <p className="q-label mb-2">How often</p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
              {[1, 3, 6].map((m) => (
                <button key={m} type="button" role="radio" aria-checked={months === m} onClick={() => setMonths(m)} data-testid={`backup-every-${m}`}
                  className={`min-h-[44px] rounded-xl border px-2 text-sm font-semibold transition-colors ${months === m ? "border-[rgb(var(--glow-family))] bg-[rgb(var(--glow-family)/0.15)] text-q-text" : "border-q-line text-q-muted hover:text-q-text"}`}>
                  {m === 1 ? "Every month" : `Every ${m} months`}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-q-line p-3">
            <span>
              <span className="block font-semibold text-q-text">Include call videos</span>
              <span className="text-sm text-q-muted" data-testid="backup-video-size">{b.video_count ? `${b.video_count} video${b.video_count > 1 ? "s" : ""}, about ${fmtBytes(b.video_bytes)} more` : "No call videos yet"}</span>
            </span>
            <Switch checked={videos} onCheckedChange={setVideos} data-testid="backup-videos-switch" />
          </label>
          <div className="rounded-xl border border-q-line p-3">
            <p className="font-semibold text-q-text">Backup password (optional)</p>
            <p className="mb-2 text-sm text-q-muted">
              {b.has_password ? "A backup password is set. Enter a new one to change it." : "Every backup ZIP is AES-256 protected with it. At least 8 characters. We keep it encrypted and never show it again."}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Password" error={errs?.password}><input type="password" autoComplete="new-password" className="q-input min-h-[44px] w-full" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="backup-password" /></Field>
              <Field label="Again" error={mismatch ? "The passwords don't match" : null}><input type="password" autoComplete="new-password" className="q-input min-h-[44px] w-full" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="backup-password2" /></Field>
            </div>
            {b.has_password && !pw && (
              <label className="mt-2 flex items-center gap-2 text-sm text-q-muted"><input type="checkbox" checked={removePw} onChange={(e) => setRemovePw(e.target.checked)} data-testid="backup-remove-password" /> Remove the backup password</label>
            )}
          </div>
          <p className="text-sm text-q-muted">About {fmtBytes(b.est_bytes + (videos ? b.video_bytes : 0))} each time. Each backup is ready for 7 days, then deleted. The other parents are told when you turn this on, change it or turn it off.</p>
          <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={!ok || busy} data-testid="backup-save-btn">{busy ? "Saving…" : b.on ? "Save changes" : "Turn on and back up now"}</button>
        </form>
      )}
    </FormDialog>
  );
}

export function AutoBackups({ hub, hasPassword, google }) {
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(!!google);
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.get("/backups", { params: { organization_id: hub.id } }).then(({ data }) => setB({ ...data, hub_id: hub.id })).catch(() => setB(null)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!b?.last_job_id || b.last_status !== "ready") { setJob(null); return; }
    api.get(`/exports/${b.last_job_id}`).then(({ data }) => setJob(data)).catch(() => setJob(null));
  }, [b?.last_job_id, b?.last_status]);
  useEffect(() => {
    if (!b?.running && !b?.retrying) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [b, load]);
  if (!b) return null;
  const off = async () => {
    try { const { data } = await api.put("/backups", { organization_id: hub.id, on: false }); setB({ ...b, ...data }); toast.success("Automatic backups are off"); } catch (x) { toast.error(errMsg(x)); }
  };
  const now = async () => {
    try { await api.post("/backups/now", { organization_id: hub.id }); toast.success("Backing up now. We'll let you know when it's ready."); setB({ ...b, running: true }); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="border-t border-q-line py-4" data-testid="auto-backups">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-semibold text-q-text"><CalendarClock className="h-4 w-4 text-[rgb(var(--glow-family))]" /> Automatic backups</p>
          <p className="text-sm text-q-muted"><Status b={b} /></p>
          {b.last_status === "failed" && b.on && (
            <button className="mt-1 text-sm font-semibold text-red-300 underline" onClick={now} data-testid="backup-try-now">Last backup failed · Try now</button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {b.on && b.last_status !== "failed" && (
            <button className="q-btn q-btn-ghost min-h-[44px]" onClick={now} disabled={b.running} data-testid="backup-now-btn">
              {b.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} Back up now
            </button>
          )}
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setOpen(true)} data-testid="backup-setup-btn">{b.on ? "Change" : "Turn on"}</button>
          {b.on && <button className="q-btn q-btn-ghost min-h-[44px]" onClick={off} data-testid="backup-off-btn">Turn off</button>}
        </div>
      </div>
      {!b.on && <p className="mt-1 text-xs text-q-faint" data-testid="backup-defaults">Every 3 months · call videos off · backup password optional</p>}
      {job && job.status === "ready" && <div className="mt-3 rounded-xl border border-q-line p-3" data-testid="backup-latest"><ExportProgress job={job} onDeleted={load} /></div>}
      {open && <Setup b={b} hasPassword={hasPassword} google={google} onClose={() => setOpen(false)} onSaved={(d) => { setOpen(false); setB({ ...b, ...d }); setTimeout(load, 1500); }} />}
    </div>
  );
}
